import { Box, Button, Typography, Tooltip, CircularProgress } from "@mui/material";
import { useEffect, useState } from "react";
import { useSession } from "next-auth/react";
import { getImportMetrics } from "../../actions/importActions";
import { ContextLink } from "@/components/common/ContextLink";
import { useSelect } from "@/context/ContextSelector";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { addContextToUrl } from "@/utils/contextUrl";

export default function Home() {
  const { contextValue } = useSelect();
  const router = useRouter();

  const [metrics, setMetrics] = useState({
    importCount: 0,
    sequenceCount: 0,
    distinctLocationCount: 0,
  });
  const [loadingMetrics, setLoadingMetrics] = useState(true);

  const { data } = useSession() as any;
  const isLoggedIn = !(
    data === null || data?.error === "RefreshAccessTokenError"
  );

  useEffect(() => {
    async function fetchImportMetrics() {
      setLoadingMetrics(true);
      try {
        const response = await getImportMetrics();
        setMetrics(response);
      } finally {
        setLoadingMetrics(false);
      }
    }
    fetchImportMetrics();
  }, []);

  return (
    <Box
      sx={{
        width: "100%",
        display: "flex",
        flexDirection: "column",
        gap: 2,
        px: 4,
      }}
    >
      <Typography sx={{ my: 1 }}>
        An open but regulated platform that aims at providing the community with
        both quality-certified data and tools for arbovirus sequence mining and
        analysis.
      </Typography>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexDirection: "column",
          pt: 18,
          pb: 4,
          px: 2,
          background: `url('/assets/arbovirus.png') no-repeat center center/cover`,
          width: "100%",
          height: "360px",
          color: "white",
          fontSize: "18px",
          textAlign: "center",
        }}
      >
        <Box>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 1,
              paddingTop: "!important 75px",
            }}
          >
            <Typography variant="h5">Discover the Arbogen platform</Typography>
            <Typography variant="body1">
              ARBOGEN aims to facilitate datasharing and data access by making
              available curated public virus sequence datasets.
              <br></br>
              <br></br>
              The platform combines public sequence data from the NCBI database
              and sequence data provided directly by the users that wish to make
              their data available.
            </Typography>
          </Box>
        </Box>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-evenly",
            gap: 4,
            width: "100%",
          }}
        >
          {
            [
              ["Sequences", metrics.sequenceCount],
              ["Territories represented", metrics.distinctLocationCount],
              ["Imports", metrics.importCount],
            ].map(([label, value]) => (
              <Box
                key={label}
                sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}
              >
                {loadingMetrics ? (
                  <CircularProgress
                    sx={{ color: "primary.contrastText" }}
                    size={32}
                    thickness={6}
                  />
                ) : (
                  <Typography variant="h5">{value}</Typography>
                )}
                <Typography variant="body1">{label}</Typography>
              </Box>
            ))
          }
        </Box>
      </Box>

      <Box sx={{ display: "flex", mt: 1, justifyContent: "space-around" }}>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 1,
            width: "35%",
          }}
        >
          <Typography variant="h5">Explore</Typography>
          <Typography variant="body1">
            Visualize virus genomic data in context through our aggregated{" "}
            {contextValue === "DENV" ? (
              <ContextLink href="/explore/all#map">map</ContextLink>
            ) : (
              "map"
            )}{" "}
            and{" "}
            <ContextLink href="/explore/all#histogram">histogram</ContextLink>{" "}
            visualisations.{" "}
            <ContextLink href="/explore/all">Browse</ContextLink> and{" "}
            <ContextLink href="/explore/all">download</ContextLink> public virus
            genomes under a table format.
          </Typography>
          <Tooltip
            title={!isLoggedIn ? "Please log in to access to this page" : ""}
            disableHoverListener={isLoggedIn}
            arrow
          >
            <span
              style={{
                display: "inline-block",
                width: "fit-content",
                alignSelf: "flex-start",
              }}
            >
              <Button
                variant="contained"
                sx={{
                  display: "flex",
                  width: "fit-content",
                  px: 3,
                }}
                onClick={() => router.push(addContextToUrl("/explore/all", contextValue))}
                disabled={!isLoggedIn}
              >
                Search
              </Button>
            </span>
          </Tooltip>
        </Box>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 1,
            width: "35%",
          }}
        >
          <Typography variant="h5">Contribute</Typography>
          <Typography variant="body1">
            Contribute by uploading virus sequence data under a private,
            semi-private or public status. Submit sequences from within your
            account using our detailed{" "}
            <Link href="/docs/Process Description" target="_blank">
              documentation
            </Link>{" "}
            and datasharing process.
          </Typography>
          <Button
            variant="contained"
            sx={{
              display: "flex",
              width: "fit-content",
              px: 3,
            }}
            href="/contribute"
          >
            Contribute
          </Button>
        </Box>
      </Box>
    </Box>
  );
}
